import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { AlertRecord } from '@/types';
import SeverityBadge from './SeverityBadge';
import { relativeTime, txt } from '@/lib/format';
import { SEVERITY_STYLES } from '@/lib/severity';
import { cn } from '@/lib/cn';

/**
 * Single row in the alerts feed. Click to expand context + stack.
 * Designed for high-density vertical scanning — avoid heavy borders.
 */
export default function AlertItem({ alert }: { alert: AlertRecord }) {
  const [open, setOpen] = useState(false);
  const sev = SEVERITY_STYLES[alert.severity];
  return (
    <div className={cn(
      'border-l-2 pl-3 py-2 hover:bg-slate-900/60 cursor-pointer rounded-r',
      alert.severity === 'critical' && 'border-red-500',
      alert.severity === 'error'    && 'border-orange-500',
      alert.severity === 'warn'     && 'border-yellow-500',
      alert.severity === 'info'     && 'border-sky-500',
    )}
      onClick={() => setOpen(o => !o)}
    >
      <div className="flex items-start gap-2 sm:gap-3">
        {open
          ? <ChevronDown className="w-4 h-4 mt-0.5 shrink-0 text-slate-500" />
          : <ChevronRight className="w-4 h-4 mt-0.5 shrink-0 text-slate-500" />}
        <SeverityBadge severity={alert.severity} className="mt-0.5" />
        {/* Category + message wrap together on narrow screens, stay
            inline on `sm+`. min-w-0 lets the message truncate properly. */}
        <div className="flex-1 min-w-0 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className={cn(
            'text-xs font-mono px-1.5 py-0.5 rounded shrink-0',
            sev.bg, sev.fg,
          )}>
            {txt(alert.category)}
          </span>
          <span className="text-sm text-slate-200 break-words min-w-0">
            {alert.message}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0 text-xs text-slate-500 mt-0.5">
          {alert.count && alert.count > 1 && <span>×{alert.count}</span>}
          <span className="whitespace-nowrap">{relativeTime(alert.ts)}</span>
        </div>
      </div>

      {open && (
        <div className="mt-2 ml-7 space-y-2 text-xs">
          {alert.errorMessage && (
            <div className="text-rose-300">
              <span className="text-slate-500">error: </span>{alert.errorMessage}
            </div>
          )}
          {alert.context && Object.keys(alert.context).length > 0 && (
            <pre className="bg-slate-900 border border-slate-800 rounded p-2 overflow-x-auto text-slate-300">
              {JSON.stringify(alert.context, null, 2)}
            </pre>
          )}
          {alert.errorStack && (
            <pre className="bg-slate-900 border border-slate-800 rounded p-2 overflow-x-auto text-slate-400 font-mono">
              {alert.errorStack}
            </pre>
          )}
          <div className="text-slate-500">
            id: <span className="font-mono">{alert.id}</span>
            {alert.fingerprint && (<>  ·  fingerprint: <span className="font-mono">{alert.fingerprint}</span></>)}
          </div>
        </div>
      )}
    </div>
  );
}
